// Adapter layer for the medical-staff Notification inbox (PRD 08 v1.3 — 공용 수신구).
//
// Contract reference: api-contracts/notification.md.
//
// PRD 08 v1.1+ pivoted this from a notice-only page to a SHARED INBOX:
//   ① 환자 이벤트  — 고위험(RISK), PRO 미응답(PRO_DELAY)   [+ HR_ABNORMAL, prototype-only — see note]
//   ② 운영 공지    — SYSTEM (기관 공지, 어드민 발송)
// Home 4-section dashboard = 전체현황 시각화 / this bell = 행동필요 능동 수신구 (PRD §1.1).
//
// Mock-gated by VITE_USE_MOCK. The mock branch mutates an in-module array so mark-read /
// mark-all reflect on refetch. The real branch calls httpClient via `endpoints`.

import { httpClient } from '@/api/http-client';
import { endpoints } from '@/api/endpoints';
import { ApiError, mapAxiosError } from '@/api/error';

// PRD §4.1 type enum. HR_ABNORMAL (심박수 이상) appears in the prototype TYPE_META + stories but is
// NOT in PRD 08 §4.1, and no heart-rate source exists yet — kept in the union so the UI renders it
// gracefully IF the BE ever emits it, but no filter option is dedicated to it (it falls under 환자
// 이벤트/EVENT). Pending clarification Q2 (story/prototype vs PRD). Do not treat as confirmed scope.
export type NotificationType = 'SYSTEM' | 'RISK' | 'PRO_DELAY' | 'HR_ABNORMAL';
export type NotificationStatus = 'UNREAD' | 'READ';
export type NotificationStatusFilter = 'ALL' | NotificationStatus;
// Type filter values (PRD §2.2 #2 / prototype): 전체 유형 / 환자 이벤트 / 공지.
export type NotificationTypeFilter = 'ALL' | 'EVENT' | 'SYSTEM';

/** The patient-event types grouped under the 환자 이벤트 (EVENT) filter. */
const EVENT_TYPES: NotificationType[] = ['RISK', 'PRO_DELAY', 'HR_ABNORMAL'];

export interface Notification {
  id: string;
  type: NotificationType;
  title: string;
  body: string;
  createdAt: string; // ISO-8601, KST offset +09:00 (DECISION-TIMEZONE-001) — e.g. "2026-04-22T18:00:00+09:00"
  status: NotificationStatus;
  relatedPatientId: string | null; // patient UID for RISK/HR_ABNORMAL deep-link; null for PRO_DELAY/SYSTEM
  relatedPatientName: string | null; // patient name for RISK/HR_ABNORMAL; null otherwise
}

export interface NotificationListResult {
  items: Notification[];
  unreadCount: number; // total unread across ALL notifications (drives GNB badge)
}

interface ApiSuccess<T> {
  data: T;
}

const IS_MOCK = import.meta.env.VITE_USE_MOCK === 'true';
const MOCK_LATENCY_MS = 200;
const delay = (ms: number) => new Promise<void>((resolve) => setTimeout(resolve, ms));

// ──────────────────────────────────────────────────────────────────────────────
// Deep-link target per notification type (PRD §3.2 / prototype `targetHash`).
// RISK / HR_ABNORMAL → 환자 상세 (patients list, ?selected=<uid>). PRO_DELAY → 홈 PRO 응답 지연.
// SYSTEM → null (no navigation; the card expands/collapses in place).
// BrowserRouter (not hash) → plain paths.
// ──────────────────────────────────────────────────────────────────────────────
export function notificationTarget(n: Notification): string | null {
  if ((n.type === 'RISK' || n.type === 'HR_ABNORMAL') && n.relatedPatientId) {
    return `/patients?selected=${encodeURIComponent(n.relatedPatientId)}`;
  }
  if (n.type === 'PRO_DELAY') {
    return '/home?section=delay';
  }
  return null;
}

// ──────────────────────────────────────────────────────────────────────────────
// Mock seeds — PRD §4.3 sample bodies (환자 이벤트 + 운영 공지). `let`-mutable statuses so the mock
// read / read-all branches flip them. 한동욱 = P045 (matches the home-dashboard mock patient).
// ──────────────────────────────────────────────────────────────────────────────
const MOCK_NOTIFICATIONS: Notification[] = [
  {
    id: 'N010',
    type: 'RISK',
    title: '위험환자 발생 (Grade R)',
    body: '한동욱 환자의 R값이 고위험으로 산출되었습니다. 즉시 확인이 필요합니다.',
    createdAt: '2026-04-23T17:30:00+09:00', // KST (DECISION-TIMEZONE-001)
    status: 'UNREAD',
    relatedPatientId: 'P045',
    relatedPatientName: '한동욱',
  },
  {
    id: 'N011',
    type: 'PRO_DELAY',
    title: 'PRO 미응답 환자 2명',
    body: "PRO 설문을 3일 연속 미응답한 환자: 강하늘, 차승우. 홈 'PRO 응답 지연'에서 확인하세요.",
    createdAt: '2026-04-23T09:00:00+09:00', // KST (DECISION-TIMEZONE-001)
    status: 'UNREAD',
    relatedPatientId: null,
    relatedPatientName: null,
  },
  {
    id: 'N001',
    type: 'SYSTEM',
    title: '[공지] 시스템 점검 안내',
    body: '4/27(월) 02:00~04:00 정기 시스템 점검이 예정되어 있습니다. 점검 시간 동안 대시보드 접속이 일시 중단됩니다. 점검 완료 후 정상적으로 이용하실 수 있으며, 자세한 사항은 공지사항을 참고해 주세요.',
    createdAt: '2026-04-22T18:00:00+09:00', // KST (DECISION-TIMEZONE-001)
    status: 'UNREAD',
    relatedPatientId: null,
    relatedPatientName: null,
  },
  {
    id: 'N003',
    type: 'SYSTEM',
    title: '[업데이트] 매뉴얼 v1.2 배포',
    body: 'PRO 캘린더 셀 응답 상세 보기 가이드가 추가되었습니다. 도움말 탭에서 확인하실 수 있습니다.',
    createdAt: '2026-04-19T00:00:00+09:00', // KST (DECISION-TIMEZONE-001)
    status: 'READ',
    relatedPatientId: null,
    relatedPatientName: null,
  },
  {
    id: 'N004',
    type: 'SYSTEM',
    title: '[정책] 개인정보 처리방침 개정',
    body: '4/15부터 개정된 개인정보 처리방침이 적용됩니다. 본문은 약관 및 정책 메뉴에서 확인하세요.',
    createdAt: '2026-04-14T19:00:00+09:00', // KST (DECISION-TIMEZONE-001)
    status: 'READ',
    relatedPatientId: null,
    relatedPatientName: null,
  },
];

function sortByCreatedAtDesc(list: Notification[]): Notification[] {
  return [...list].sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
  );
}

function countUnread(list: Notification[]): number {
  return list.filter((n) => n.status === 'UNREAD').length;
}

function matchesTypeFilter(n: Notification, type: NotificationTypeFilter): boolean {
  if (type === 'ALL') return true;
  if (type === 'EVENT') return EVENT_TYPES.includes(n.type);
  return n.type === type; // 'SYSTEM'
}

// ──────────────────────────────────────────────────────────────────────────────
// GET /api/v1/medical/notifications?type=ALL|EVENT|SYSTEM&status=ALL|UNREAD|READ
// ──────────────────────────────────────────────────────────────────────────────
export async function fetchNotifications(
  type: NotificationTypeFilter = 'ALL',
  status: NotificationStatusFilter = 'ALL',
): Promise<NotificationListResult> {
  if (IS_MOCK) {
    await delay(MOCK_LATENCY_MS);
    const filtered = MOCK_NOTIFICATIONS.filter(
      (n) =>
        matchesTypeFilter(n, type) && (status === 'ALL' || n.status === status),
    );
    return {
      items: sortByCreatedAtDesc(filtered),
      // Always total unread, independent of the filters (contract §3.1).
      unreadCount: countUnread(MOCK_NOTIFICATIONS),
    };
  }
  try {
    const { data } = await httpClient.get<ApiSuccess<NotificationListResult>>(
      endpoints.medicalStaff.notifications,
      { params: { type, status } },
    );
    return {
      items: sortByCreatedAtDesc(
        data.data.items.map((n) => ({
          ...n,
          relatedPatientId: n.relatedPatientId ?? null,
          relatedPatientName: n.relatedPatientName ?? null,
        })),
      ),
      unreadCount: data.data.unreadCount,
    };
  } catch (err) {
    throw mapAxiosError(err);
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// POST /api/v1/medical/notifications/{id}/read
// ──────────────────────────────────────────────────────────────────────────────
export async function markNotificationRead(id: string): Promise<void> {
  if (IS_MOCK) {
    await delay(MOCK_LATENCY_MS);
    const hit = MOCK_NOTIFICATIONS.find((n) => n.id === id);
    if (!hit) {
      throw new ApiError('NOT_FOUND', '요청한 알림을 찾을 수 없습니다.');
    }
    hit.status = 'READ';
    return;
  }
  try {
    await httpClient.post(endpoints.medicalStaff.notificationRead(id));
  } catch (err) {
    throw mapAxiosError(err);
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// POST /api/v1/medical/notifications/{id}/view  — SHCT-652 (조회수)
//
// NOT the same event as `/read`. 어드민 PRD 13 §3.1 defines 조회수 as "환자/의료진이 앱 또는 웹에서
// 해당 공지사항 상세 화면에 진입할 때마다 1씩 증가" — every open, so a re-open by the same staff counts
// again. `/read` is once-per-staff by design and therefore cannot carry it.
//
// Only meaningful for SYSTEM (공지) rows, whose "상세 진입" is the in-place body expand (PRD 08 v1.1
// §3.2). Fire-and-forget: the counter is admin-side telemetry the medical UI never renders, so a
// failure must not interrupt the staff — the caller swallows the error.
// ──────────────────────────────────────────────────────────────────────────────
export async function markNotificationViewed(id: string): Promise<void> {
  if (IS_MOCK) {
    await delay(MOCK_LATENCY_MS);
    // 조회수 lives on ad_notice (admin-owned) and is never surfaced in medical-web, so there is no
    // mock state to mutate — the mock branch just resolves.
    return;
  }
  try {
    await httpClient.post(endpoints.medicalStaff.notificationView(id));
  } catch (err) {
    throw mapAxiosError(err);
  }
}

// ──────────────────────────────────────────────────────────────────────────────
// POST /api/v1/medical/notifications/read-all
// ──────────────────────────────────────────────────────────────────────────────
export async function markAllNotificationsRead(): Promise<void> {
  if (IS_MOCK) {
    await delay(MOCK_LATENCY_MS);
    MOCK_NOTIFICATIONS.forEach((n) => {
      n.status = 'READ';
    });
    return;
  }
  try {
    await httpClient.post(endpoints.medicalStaff.notificationsReadAll);
  } catch (err) {
    throw mapAxiosError(err);
  }
}
